import styles from './RequestProjectAccessDialog.module.css';

import useTranslation from 'components/hooks/useTranslation';
import { useCallback, useMemo, useState } from 'react';
import useFetchData from 'components/hooks/useFetchData';
import Api from 'components/Api';
import BngButton from 'components/bng/ui/BngButton';
import Icon from 'components/ui/common/Icon';
import Dialog from 'components/ui/Dialog';
import BngTable from 'components/bng/ui/BngTable';
import UiMsg from 'components/ui/UiMsg';

export default function RequestProjectAccessDialog({
  closeModal = _.noop,
  projectsThatTrustDomain = [],
  projectToRequestFromUrl = [],
  userEmail = '',
}) {
  const { t } = useTranslation();
  const isProjectFromUrl = projectToRequestFromUrl.length > 0;
  const [projectsToSendRequest, setProjectsToSendRequest] = useState(isProjectFromUrl ? projectToRequestFromUrl : []);
  const [loading, setLoading] = useState(false);

  const { data: projectsAlreadyRequested, isLoading: pendingRequest } = useFetchData(
    async ([email]) => {
      const requestsForCurrentEmail = await Api.ProjectAccessRequest.findPendingRequests({ email });
      return requestsForCurrentEmail.map((request) => request.projectId);
    },
    [userEmail]
  );

  const handleRequestAccess = useCallback(
    async (project) => {
      setLoading(true);
      try {
        await Api.ProjectAccessRequest.requestAccess({
          projectIds: [project.id],
          email: userEmail,
        });
        setProjectsToSendRequest((prev) => [...prev, project]);
        UiMsg.ok(t('request.successfully.sent'));
      } catch (e) {
        console.error('Error on requestAccess()', e);
        UiMsg.error('request.error', e);
      } finally {
        setLoading(false);
      }
    },
    [userEmail]
  );

  const cols = useMemo(() => {
    return [
      {
        key: 'projectName',
        size: '70%',
        render: (row) => {
          return (
            <div className="ml-1">
              <img
                alt="projectIcon"
                src={
                  row.favIcon
                    ? Api.buildUrl(`/projects/${row.name}/${row.favIcon}`)
                    : Api.buildUrl(`/resources/images/favicon-menu-projects.png`)
                }
                style={{ verticalAlign: 'sub' }}
              />
              <span className="ml-1">{row.displayName}</span>
            </div>
          );
        },
      },
      {
        key: 'requestButton',
        size: '30%',
        render: (row) => {
          if (pendingRequest) {
            return;
          }

          const alreadyRequested = (projectsAlreadyRequested ?? []).includes(row.id);

          if (alreadyRequested) {
            return (
              <BngButton className={`${styles.requestButton} ${styles.selected}`}>
                <Icon icon={'done'}></Icon>
                {t('requested')}
              </BngButton>
            );
          } else {
            const isSelected = projectsToSendRequest.some((project) => project.id === row.id);
            return (
              <BngButton
                className={`${styles.requestButton} ${isSelected ? styles.selected : ''}`}
                onClick={isSelected ? undefined : () => handleRequestAccess(row)}
              >
                <Icon icon={isSelected ? 'done' : 'login'}></Icon>
                {isSelected ? t('requested') : t('request.access')}
              </BngButton>
            );
          }
        },
      },
    ];
  }, [projectsThatTrustDomain, projectsToSendRequest, projectsAlreadyRequested, handleRequestAccess, pendingRequest]);

  return (
    <Dialog
      className={`RequestProjectAccessDialog ${styles.RequestProjectAccessDialog}`}
      title={t('welcome.to.bim')}
      onClose={closeModal}
      loading={loading || pendingRequest}
    >
      <Dialog.Body>
        <div style={{ lineHeight: '0' }}>
          <label className="fw-500 mb-0">
            {isProjectFromUrl ? t('request.project.access.label2') : t('request.project.access.label1')}
          </label>
          <br />
          <p style={{ marginTop: '16px' }}>{isProjectFromUrl ? '' : t('request.project.access.description')}</p>
        </div>
        <div>
          <div className={styles.ProjectsTableContainer}>
            <BngTable className={styles.ProjectsTable} cols={cols} rows={projectsThatTrustDomain} stickyHeader={true} />
          </div>
          <span className={styles.requestAnswerText}>{t('request.project.access.msg')}</span>
        </div>
        <div
          className={styles.startTrialButton}
          dangerouslySetInnerHTML={{ __html: t('request.project.access.register') }}
        />
      </Dialog.Body>
    </Dialog>
  );
}
